import React, { useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useSiteContent } from "../context/SiteContentContext";
import { ArrowLeft, Calendar, Tag } from "lucide-react";

function findPost(posts, idParam) {
  if (!Array.isArray(posts)) return null;
  const asNumber = Number(idParam);
  if (Number.isFinite(asNumber)) return posts.find((p) => Number(p?.id) === asNumber) || null;
  const slug = String(idParam || "").toLowerCase();
  return posts.find((p) => String(p?.slug || "").toLowerCase() === slug) || null;
}

export default function BlogPostPage() {
  const { id } = useParams();
  const location = useLocation();
  const { siteContent } = useSiteContent();

  const post = useMemo(() => {
    return (
      findPost(siteContent?.blogPosts, id) ||
      (location?.state?.post ? location.state.post : null)
    );
  }, [siteContent?.blogPosts, id, location?.state?.post]);

  if (!post) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Card className="border-0 shadow-lg shadow-slate-200/50">
            <CardContent className="p-8">
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Artigo não encontrado</h1>
              <p className="text-slate-600 mb-6">O artigo que você tentou abrir não existe ou foi removido.</p>
              <Button asChild className="bg-orange-500 hover:bg-orange-600">
                <Link to="/blog">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar para o blog
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const dateLabel = post?.date ? new Date(post.date).toLocaleDateString("pt-BR") : "";
  const content = post?.content || post?.body || post?.excerpt || "";

  return (
    <Layout>
      <article>
        <header className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          {post?.image ? (
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url(${post.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          ) : null}
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="flex items-center gap-3 mb-6">
              <Button asChild variant="secondary" className="bg-white/10 text-white hover:bg-white/15">
                <Link to="/blog">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Blog
                </Link>
              </Button>
              {post?.category ? (
                <Badge className="bg-orange-500/20 text-orange-200 border border-orange-500/30">
                  <Tag className="w-3 h-3 mr-2" />
                  {post.category}
                </Badge>
              ) : null}
              {dateLabel ? (
                <span className="inline-flex items-center gap-2 text-white/70 text-sm">
                  <Calendar className="w-4 h-4" />
                  {dateLabel}
                </span>
              ) : null}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">{post.title}</h1>
            {post?.excerpt ? <p className="mt-6 text-lg text-slate-200/90">{post.excerpt}</p> : null}
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-slate max-w-none">
            {String(content)
              .split("\n")
              .filter((p) => p.trim().length > 0)
              .map((p, idx) => (
                <p key={idx} className="text-slate-700 leading-relaxed">
                  {p}
                </p>
              ))}
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <Button asChild variant="outline" className="border-slate-200">
              <Link to="/blog">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para o blog
              </Link>
            </Button>
          </div>
        </div>
      </article>
    </Layout>
  );
}
